/**
 * QueueHeader - Page title, points badge/explainer and sound controls
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, BellOff, Settings, CircleQuestionMark, Star } from 'lucide-react';

interface QueueHeaderProps {
  showPoints: boolean;
  points: number;
  soundEnabled: boolean;
  onToggleSound: () => void;
  onToggleSoundSettings: () => void;
}

export function QueueHeader({
  showPoints,
  points,
  soundEnabled,
  onToggleSound,
  onToggleSoundSettings,
}: QueueHeaderProps) {
  const { t } = useTranslation();
  const [showPointsExplainer, setShowPointsExplainer] = useState(false);

  return (
    <div className="flex justify-between items-center mb-8">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl text-ink">Cola Virtual</h1>
        {/* User points display - only show if points system is enabled */}
        {showPoints && (
          <div className="mt-2 flex items-center gap-2">
            <div className="inline-flex items-center gap-1.5 bg-accent/10 text-accent px-3 py-1.5 rounded-full border border-accent/30">
              <Star className="w-4 h-4" aria-hidden="true" />
              <span className="text-sm font-semibold tabular-nums">{points} puntos</span>
            </div>
            <button
              onClick={() => setShowPointsExplainer((prev) => !prev)}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full bg-surface-2 text-ink-subtle hover:text-ink hover:bg-surface-3 transition-colors"
              aria-label={t('loyalty.pointsExplainer.title')}
              aria-expanded={showPointsExplainer}
            >
              <CircleQuestionMark className="w-6 h-6" aria-hidden="true" />
            </button>
          </div>
        )}
        {showPoints && showPointsExplainer && (
          <div
            role="dialog"
            aria-label={t('loyalty.pointsExplainer.title')}
            className="mt-3 max-w-md bg-surface border border-hairline rounded-xl p-4"
          >
            <h2 className="font-sans text-lg font-semibold text-ink mb-2">
              {t('loyalty.pointsExplainer.title')}
            </h2>
            <p className="text-sm text-ink-muted mb-3">
              {t('loyalty.pointsExplainer.body')}
            </p>
            <button
              onClick={() => setShowPointsExplainer(false)}
              className="w-full min-h-[44px] bg-accent hover:bg-accent-hover text-on-accent font-semibold rounded-lg px-4 py-2 transition-colors"
            >
              {t('loyalty.pointsExplainer.gotIt')}
            </button>
          </div>
        )}
      </div>

      {/* Sound controls */}
      <div className="flex items-center gap-2">
        {/* Quick sound toggle */}
        <button
          onClick={onToggleSound}
          className={`p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg transition-colors ${
            soundEnabled
              ? 'bg-accent/10 text-accent hover:bg-accent/20'
              : 'bg-surface-2 text-ink-subtle hover:text-ink hover:bg-surface-3'
          }`}
          title={soundEnabled ? 'Sonidos activados' : 'Sonidos desactivados'}
          aria-label={soundEnabled ? 'Desactivar sonidos' : 'Activar sonidos'}
        >
          {soundEnabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
        </button>

        {/* Sound settings button */}
        <button
          onClick={onToggleSoundSettings}
          className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg bg-surface-2 text-ink-muted hover:text-ink hover:bg-surface-3 transition-colors"
          title="Configuración de sonidos"
          aria-label="Configuración de sonidos"
        >
          <Settings className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
